import { useEffect, useRef } from 'react';

/**
 * Debounced auto-save hook. Triggers `saveFn` after `delayMs` of inactivity
 * whenever `deps` change. Skips the initial render to avoid saving on mount.
 */
export function useAutoSave(saveFn: () => Promise<void>, deps: unknown[], delayMs = 2000): void {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return undefined;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      saveFn().catch(() => {
        // Auto-save failures are non-critical; the user will be prompted on next navigation
      });
    }, delayMs);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
}
